import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core'
import { SiteMembership } from '@se-po/shared-data-access-models'
import {
  AnalyticsService,
  SiteMembershipService,
  SurveyFindAllParams,
  SurveyService,
  SurveyStatus,
} from '@se-po/shared-data-access-services'
import { forkJoin, Observable, of, Subscription, timer } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { Survey } from 'se-resource-types/dist/lib/Ngin'
import {
  OpenRegistrationFilterAction,
  OpenRegistrationFilterKey,
  OpenRegistrationFilterOption,
  OpenRegistrationFilters,
} from '../open-registration-filter/open-registration-filter.component'

export interface OpenRegistrationOrganization {
  organizationId: string | number
  organizationName: string
  organizationColor: string
  organizationLogoUrl: string
  registrations: Survey[]
}

type SurveyWithSportType = Survey & {
  sport_key?: string
  sport?: string
  signup_type_list?: string[]
}

type SiteLogoGraphic = {
  medium_icon_url?: string | null
  medium_logo_url?: string | null
  url?: string | null
}

export interface OpenRegistrationInfoText {
  key: string
  params?: Record<string, string>
}

@Component({
  selector: 'se-open-registration-list',
  templateUrl: './open-registration-list.component.html',
  styleUrls: ['./open-registration-list.component.scss'],
  standalone: false,
})
export class OpenRegistrationListComponent
  implements OnDestroy, OnInit
{
  public readonly translationKey = 'SE_PO_SE_REGISTRATION_UI_FEATURE_OPEN_REGISTRATION_LIST.'
  public registrationGroups: OpenRegistrationOrganization[] = []
  public loadedRegistrationGroups: OpenRegistrationOrganization[] = []
  public organizations: OpenRegistrationFilterOption[] = []
  public sportOptions: OpenRegistrationFilterOption[] = []
  public signupTypeList: OpenRegistrationFilterOption[] = []
  public searchOrganizations: OpenRegistrationFilterOption[] = []
  public searchSportOptions: OpenRegistrationFilterOption[] = []
  public searchSignupTypeList: OpenRegistrationFilterOption[] = []
  public hasLoaded = false
  public isLoading = false
  public loadError = false
  public activeSports: string[] = []
  public activeTypes: string[] = []
  public activeRegistrationNameLike = ''
  public appliedOrganizationIdFilters: string[] = []
  public clearFiltersRequestId = 0

  private siteMembershipService = inject(SiteMembershipService)
  private surveyService = inject(SurveyService)
  private analyticsService = inject(AnalyticsService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private registrationsSubscription?: Subscription
  private searchTermSubscription?: Subscription
  private hasLoadedRegistrationGroups = false
  private searchDataUpdateId = 0
  private searchDataUpdateTimeout?: ReturnType<typeof setTimeout>

  public ngOnInit(): void {
    this.loadedRegistrationGroups = []
    this.hasLoadedRegistrationGroups = false
    this.loadRegistrations()
  }

  public ngOnDestroy(): void {
    this.registrationsSubscription?.unsubscribe()
    this.searchTermSubscription?.unsubscribe()
    this.cancelSearchDataUpdate()
  }

  public get filteredRegistrationGroups(): OpenRegistrationOrganization[] {
    return this.registrationGroups
  }

  public get hasActiveSearchCriteria(): boolean {
    return Boolean(
      this.activeRegistrationNameLike ||
      this.activeSports.length ||
      this.activeTypes.length ||
      this.appliedOrganizationIdFilters.length,
    )
  }

  public registrationSportType(registration: Survey): string {
    const registrationWithSportType = registration as SurveyWithSportType

    if (!registrationWithSportType.sport_key) return ''

    return registrationWithSportType.sport
  }

  public registrationSignupTypes(registration: Survey): string {
    const registrationWithSportType = registration as SurveyWithSportType

    return registrationWithSportType.signup_type_list?.length ? registrationWithSportType.signup_type_list.join(' / ') : ''
  }

  public registrationInfoText(registration: Survey): OpenRegistrationInfoText {
    const openDate = registration.open_date
    const closeDate = registration.close_date

    if (openDate && closeDate) {
      return {
        key: 'STATUS.opens_and_closes',
        params: {
          closeDate: this.formatRegistrationDate(closeDate),
          openDate: this.formatRegistrationDate(openDate),
        },
      }
    }

    if (openDate) {
      return {
        key: 'STATUS.opens',
        params: { openDate: this.formatRegistrationDate(openDate) },
      }
    }

    if (closeDate) {
      return {
        key: 'STATUS.closes',
        params: { closeDate: this.formatRegistrationDate(closeDate) },
      }
    }

    return { key: 'STATUS.open' }
  }

  private formatRegistrationDate(date: string): string {
    const dateParts = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (dateParts) return `${dateParts[2]}/${dateParts[3]}/${dateParts[1]}`

    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  public organizationInitial(organizationName: string): string {
    return organizationName.trim().charAt(0).toUpperCase() || 'O'
  }

  public handleOrganizationLogoError(
    organization: OpenRegistrationOrganization,
  ): void {
    organization.organizationLogoUrl = ''
  }

  private loadRegistrations(): void {
    this.registrationsSubscription?.unsubscribe()

    this.hasLoaded = true
    this.isLoading = true
    this.loadError = false
    this.registrationGroups = []

    this.registrationsSubscription = this.loadRegistrationGroups().subscribe({
      next: (orgs) => {
        if (this.shouldUpdateLoadedRegistrationGroups()) {
          this.loadedRegistrationGroups = orgs
          this.hasLoadedRegistrationGroups = true
        }        
        this.registrationGroups = orgs.filter(
          (orgs) => orgs.registrations.length,
        )

        if (this.shouldBuildSearchData()) {
          this.buildSearchData()
          return
        }

        this.isLoading = false
        this.changeDetectorRef.detectChanges()
      },
      error: () => {
        this.registrationGroups = []
        this.organizations = []
        this.clearSearchData()
        this.isLoading = false
        this.loadError = true
        this.changeDetectorRef.detectChanges()
      },
    })
  }

  private buildSearchData(): void {
    this.buildOrganizationFilterOptions()
    this.buildSportsAndSignupTypes()
    this.queueSearchDataUpdate()
    this.isLoading = false
    this.changeDetectorRef.detectChanges()
  }

  private queueSearchDataUpdate(): void {
    const updateId = ++this.searchDataUpdateId
    const organizations = [...this.organizations]
    const sportOptions = [...this.sportOptions]
    const signupTypeList = [...this.signupTypeList]

    this.cancelSearchDataUpdate()
    this.searchDataUpdateTimeout = setTimeout(() => {
      if (updateId !== this.searchDataUpdateId) return

      this.searchOrganizations = organizations
      this.searchSportOptions = sportOptions
      this.searchSignupTypeList = signupTypeList
      this.changeDetectorRef.detectChanges()
      this.searchDataUpdateTimeout = undefined
    }, 0)
  }

  private clearSearchData(): void {
    this.searchDataUpdateId += 1
    this.cancelSearchDataUpdate()
    this.searchOrganizations = []
    this.searchSportOptions = []
    this.searchSignupTypeList = []
  }

  private cancelSearchDataUpdate(): void {
    if (!this.searchDataUpdateTimeout) return

    clearTimeout(this.searchDataUpdateTimeout)
    this.searchDataUpdateTimeout = undefined
  }

  private shouldBuildSearchData(): boolean {
    return (
      !this.organizations.length ||
      !this.sportOptions.length ||
      !this.signupTypeList.length
    )
  }

  private buildSportsAndSignupTypes(): void {
    const sports = new Map<string, OpenRegistrationFilterOption>()
    const signupTypes = new Map<string, OpenRegistrationFilterOption>()

    this.registrationGroups.forEach((group) => {
      group.registrations.forEach((registration) => {
        this.addRegistrationFilterOptions(
          registration as SurveyWithSportType,
          sports,
          signupTypes,
        )
      })
    })

    this.sportOptions = Array.from(sports.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
    this.signupTypeList = Array.from(signupTypes.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  }

  private addRegistrationFilterOptions(
    registration: SurveyWithSportType,
    sports: Map<string, OpenRegistrationFilterOption>,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    this.addSportOption(registration, sports)
    this.addSignupTypeOptions(registration.signup_type_list, signupTypes)
  }

  private addSportOption(registration: SurveyWithSportType,
    sports: Map<string, OpenRegistrationFilterOption>,
  ): void {
    if (!registration.sport_key || sports.has(registration.sport_key)) return
    sports.set(registration.sport_key, {
      id: registration.sport_key,
      name: registration.sport,
    })
  }

  private addSignupTypeOptions(
    signupTypeList: string[] | undefined,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    signupTypeList?.forEach((signupType) => {
      this.addSignupTypeOption(signupType, signupTypes)
    })
  }

  private addSignupTypeOption(
    signupType: string | undefined,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    if (!signupType || signupTypes.has(signupType)) return

    signupTypes.set(signupType, {
      id: signupType,
      name: signupType,
    })
  }

  private buildOrganizationFilterOptions(): void {
    this.organizations = this.registrationGroups
      .map((organization) => ({
        id: organization.organizationId.toString(),
        name: organization.organizationName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  private loadRegistrationGroups(): Observable<OpenRegistrationOrganization[]> {
    const selectedOrganizations = this.buildSelectedOrganizations()
    if (selectedOrganizations.length) {
      return this.loadOrganizationRegistrations(selectedOrganizations)
    }

    if (this.hasLoadedRegistrationGroups) {
      return this.loadOrganizationRegistrations(this.loadedRegistrationGroups)
    }

    return this.siteMembershipService.mine().pipe(
      map((siteMemberships) =>
        this.buildAccessibleOrganizations(siteMemberships),
      ),
      switchMap((organizations) =>
        this.loadOrganizationRegistrations(organizations),
      ),
    )
  }

  private shouldUpdateLoadedRegistrationGroups(): boolean {
    return (
      !this.appliedOrganizationIdFilters.length &&
      !this.activeRegistrationNameLike &&
      !this.activeSports.length &&
      !this.activeTypes.length
    )
  }

  private loadOrganizationRegistrations(
    organizations: Omit<OpenRegistrationOrganization, 'registrations'>[],
  ): Observable<OpenRegistrationOrganization[]> {
    if (!organizations.length) return of([])

    return forkJoin(
      organizations.map((organization) =>
        this.surveyService
          .findAll(this.buildFindAllParams(organization.organizationId))
          .pipe(
            map((registrations) =>
              this.buildOpenRegistrationOrganization(organization, registrations),
            ),
          ),
      ),
    )
  }

  private buildOpenRegistrationOrganization(
    organization: Omit<OpenRegistrationOrganization, 'registrations'>,
    registrations: Survey[],
  ): OpenRegistrationOrganization {
    return {
      ...organization,
      organizationName:
        organization.organizationName ||
        registrations[0]?.site_name ||
        'Organization',
      registrations,
    }
  }

  private buildSelectedOrganizations(): Omit<OpenRegistrationOrganization,'registrations'>[] {
    return this.appliedOrganizationIdFilters
      .filter((organizationId) => this.hasOrganizationId(organizationId))
      .map((organizationId) => {
        const loadedOrganization = this.searchOrganizationLoaded(organizationId)
        const filterOption = this.organizations.find(
          (organization) => organization.id === organizationId.toString(),
        )

        return {
          organizationId,
          organizationName:
            loadedOrganization?.organizationName || filterOption?.name || '',
          organizationColor: loadedOrganization?.organizationColor || '',
          organizationLogoUrl: loadedOrganization?.organizationLogoUrl || '',
        }
      })
  }

  public searchOrganizationLoaded(
    organizationIdValue: number | string,
  ): Omit<OpenRegistrationOrganization, 'registrations'> | null {
    const organizationId = organizationIdValue.toString()
    const matchedGroup = this.loadedRegistrationGroups.find(
      (group) => group.organizationId.toString() === organizationId,
    )

    return matchedGroup || null
  }

  private buildAccessibleOrganizations(
    siteMemberships: SiteMembership[],
  ): Omit<OpenRegistrationOrganization, 'registrations'>[] {
    const organizations = new Map<
      number,
      Omit<OpenRegistrationOrganization, 'registrations'>
    >()

    siteMemberships.forEach((siteMembership) => {
      if (organizations.has(siteMembership.organization_id)) return

      organizations.set(siteMembership.organization_id, {
        organizationId: siteMembership.organization_id,
        organizationName: siteMembership.organization_name,
        organizationColor: siteMembership.organization_color,
        organizationLogoUrl: this.buildOrganizationLogoUrl(siteMembership),
      })
    })

    return [...organizations.values()]
  }

  private buildOrganizationLogoUrl(siteMembership: SiteMembership): string {
    const siteLogoGraphic = siteMembership.site_logo_graphic as SiteLogoGraphic | null | undefined    
    return (
      siteLogoGraphic?.medium_icon_url ||
      siteLogoGraphic?.medium_logo_url ||
      siteLogoGraphic?.url ||
      siteMembership.organization_logo?.medium_logo_url ||
      ''
    )
  }

  private buildFindAllParams(
    organizationId: string | number,
  ): SurveyFindAllParams {
    const params: SurveyFindAllParams = {
      organization_id: organizationId,
      status: SurveyStatus.Enabled,
      status_not: SurveyStatus.Archived,
      per_page: 1000,
      exclude_forms: true,
      exclude_team_only_surveys: true,
      'survey_extra[available_to_registration_list]': true,
      currently_open: true,
      order_by: 'created_at',
      direction: 'desc',
      ...(this.activeRegistrationNameLike && { search: this.activeRegistrationNameLike }),
    }
    this.addCommaSeparatedParam(params, 'sport', this.activeSports)
    this.addCommaSeparatedParam(params, 'signup_types', this.activeTypes)

    return params
  }

  private addCommaSeparatedParam(
    params: SurveyFindAllParams,
    key: 'sport' | 'signup_types',
    values: string[] = [],
  ): void {
    const joinedValues = values
      .map((value) => value.trim())
      .filter(Boolean)
      .join(',')

    if (joinedValues) params[key] = joinedValues
  }

  private hasOrganizationId(
    organizationId: string | number | null | undefined,
  ): organizationId is string | number {
    return (
      organizationId !== undefined &&
      organizationId !== null &&
      organizationId.toString().trim() !== ''
    )
  }

  public handleSearchTermChange(searchTerm: string): void {
    this.trackOpenRegistrationClick('OpenRegistration-Search')
    this.activeRegistrationNameLike = searchTerm
    this.searchTermSubscription?.unsubscribe()
    this.searchTermSubscription = timer(300).subscribe(() => {
      this.loadRegistrations()
    })
  }

  public handleFiltersChange(filters: OpenRegistrationFilters): void {
    const organizationFilters = [...(filters.organization || [])]
    const sportFilters = [...(filters.sport || [])]
    const typeFilters = [...(filters.type || [])]

    this.appliedOrganizationIdFilters = organizationFilters
    this.activeSports = sportFilters
    this.activeTypes = typeFilters
    this.searchTermSubscription?.unsubscribe()
    this.loadRegistrations()
  }

  public handleFilterAction(action: OpenRegistrationFilterAction): void {
    this.trackOpenRegistrationFilterAction(action.filterKey)
  }

  public handleClearFilters(): void {
    this.clearFiltersRequestId += 1
  }

  public handleRegistrationSignupClick(): void {
    this.trackOpenRegistrationClick('OpenRegistration-Signup')
  }

  public handleRegistrationShareClick(): void {
    this.trackOpenRegistrationClick('OpenRegistration-Share')
  }

  public handleFiltersCleared(): void {
    this.trackChangedFilters([], [], [])
    this.activeRegistrationNameLike = ''
    this.activeSports = []
    this.activeTypes = []
    this.appliedOrganizationIdFilters = []
    this.searchTermSubscription?.unsubscribe()
    this.loadRegistrations()
  }

  private trackChangedFilters(
    organizationFilters: string[],
    sportFilters: string[],
    typeFilters: string[],
  ): void {
    if (this.filterValuesChanged(this.activeSports, sportFilters)) {
      this.trackOpenRegistrationFilterAction('sport')
    }

    if (this.filterValuesChanged(this.appliedOrganizationIdFilters, organizationFilters)) {
      this.trackOpenRegistrationFilterAction('organization')
    }

    if (this.filterValuesChanged(this.activeTypes, typeFilters)) {
      this.trackOpenRegistrationFilterAction('type')
    }
  }

  private trackOpenRegistrationFilterAction(filterKey: OpenRegistrationFilterKey): void {
    const actionByFilterKey: Partial<Record<OpenRegistrationFilterKey, string>> = {
      organization: 'OpenRegistration-Filter-Organization',
      sport: 'OpenRegistration-Filter-Sport',
      type: 'OpenRegistration-Filter-Type',
    }
    const action = actionByFilterKey[filterKey]

    if (action) this.trackOpenRegistrationClick(action)
  }

  private filterValuesChanged(previousValues: string[], nextValues: string[]): boolean {
    return previousValues.length !== nextValues.length ||
      previousValues.some((value, index) => value !== nextValues[index])
  }

  private trackOpenRegistrationClick(action: string): void {
    this.analyticsService.seEvent(action)
  }
}
